import type { Middleware } from '@reduxjs/toolkit';
import { isRejectedWithValue } from '@reduxjs/toolkit';
import { useNotification } from 'src/hooks/useNotification';
import { getErrorMessage } from 'src/utils/get-error-message';
import { ROLES_PRETTY } from 'src/constants/roles';
import type { Role } from 'src/constants/roles';
import {
  GET_FRESHDESK_AGENTS_FRIENDLY_ERROR,
  GET_FRESHDESK_GROUPS_FRIENDLY_ERROR,
  INTERNAL_SERVER_ERROR_MESSAGE,
} from 'src/shared/constants';

const getUserErrorMessage = (error: {
  data?: { message?: string | string[] };
  message?: string;
  error?: string;
}): string => {
  let errorMessage = getErrorMessage(error);

  for (const key in ROLES_PRETTY) {
    errorMessage = errorMessage.replace(key, ROLES_PRETTY[key as Role]);
  }

  return errorMessage;
};

export const errorNotificationMiddleware: Middleware = () => (next) => (action: unknown) => {
  const notification = useNotification();

  if (isRejectedWithValue(action)) {
    const act = action as {
      meta: { arg: { endpointName: string } };
      payload: { status?: number; data?: { message?: string } };
    };
    const endpointName = act.meta.arg.endpointName;
    const isLoginEndpoint = endpointName === 'token';

    if (act?.payload?.status === 401 && !isLoginEndpoint) {
      return next(action);
    }

    switch (endpointName) {
      case 'updateUser':
      case 'createUser':
        notification.error(getUserErrorMessage(act.payload));
        break;
      case 'getCoCustomerAggregated':
      case 'getCoCustomerTimeSeries':
        act.payload.status !== 404 && notification.error(getErrorMessage(act.payload));
        break;
      case 'getFreshdeskEmailGroups': {
        const message = getErrorMessage(act.payload);

        if (act.payload.status === 400 && message === INTERNAL_SERVER_ERROR_MESSAGE) {
          notification.error(GET_FRESHDESK_GROUPS_FRIENDLY_ERROR);
        }
        break;
      }
      case 'getFreshdeskEmailAgents': {
        const message = getErrorMessage(act.payload);

        if (act.payload.status === 400 && message === INTERNAL_SERVER_ERROR_MESSAGE) {
          notification.error(GET_FRESHDESK_AGENTS_FRIENDLY_ERROR);
        }
        break;
      }
      default:
        notification.error(getErrorMessage(act.payload));
    }
  }

  return next(action);
};
